import type { IServerSideGetRowsParams } from 'ag-grid-community';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InputType } from '@miq/catalog';
import {
  DataObject,
  dateComparator,
  numberComparator,
  standardComparator,
  stringComparator,
  CountryId,
  HK_CHINA_COUNTRY_IDS, AgFilterParams, AgGridOptions, AgGridOptionsDomLayout, AgGridOptionsEditType, AgRowModelType, AgStreamParams, AgStreamResult, MiqAgColumn } from '@miq/shared-models';
import { AG_CELL_COMPONENTS, AG_GRID_LICENSE_KEY, UserInfoResolver } from '@miq/platform';
import { AdvancedInputFactory } from '../catalog/advanced-input-factory';
import { L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { asyncScheduler, Observable, Subject } from 'rxjs';
import { debounceTime, observeOn } from 'rxjs/operators';
import { InputTypeToCellEditorMap } from './ag-cell-renderer-map';
import { AgScrollbarDirective } from './ag-scrollbar.directive';
import { NgClass, TitleCasePipe } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import { StopKeydownDirective } from '../directives/stop-keydown.directive';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';

/**
 * One-shot, lazy ag-grid Enterprise setup. `import('ag-grid-enterprise')` must be DYNAMIC and
 * must stay out of this file's top-level imports: platform-ui is one flat FESM, and the host's
 * app.config.ts eagerly imports unrelated symbols from it, so a static import here puts the whole
 * ag-grid-enterprise chunk (~1.8 MB raw) on the boot critical path (the Datadog >15s initial-load
 * regression). Importing the package also SIDE-EFFECT-registers every enterprise module (this
 * grid uses the enterprise server-side row model), so the template gates <ag-grid-angular> on
 * {@link AgTableComponent.agGridEnterpriseReady} — the grid may not instantiate before this
 * promise resolves. The license key arrives via AG_GRID_LICENSE_KEY (a library cannot read
 * `@env`); see the token's doc for why this is not an APP_INITIALIZER.
 */
let agGridEnterprise$: Promise<void> | undefined;
function ensureAgGridEnterprise(licenseKey: string | null): Promise<void> {
  agGridEnterprise$ ??= import('ag-grid-enterprise').then(({ LicenseManager }) => {
    if (licenseKey) {
      LicenseManager.setLicenseKey(licenseKey);
    }
  });
  return agGridEnterprise$;
}
const defaultOptions = {
  sortable: false,
  resizable: false,
  filter: false,
  wrapHeaderText: true,
};
const defaultGridOptions: Partial<AgGridOptions> = {
  pagination: false,
  editType: AgGridOptionsEditType.OneAtATime,
  autoSizeAll: true,
  paginationPageSize: 0,
};
/**
 * L2-S5: moved verbatim out of `@shared/components/table/ag-table.component.ts`, which is now a
 * re-export shim. The reports remote renders it from four corporate report tables and reads
 * `filterChanged` / `sortChanged` off a `viewChild` in national-table, so `exportAs: 'miqAgTable'`
 * and every output are preserved.
 *
 * Two host edges had to be INVERTED rather than followed here, both because they are module-eval
 * couplings a library cannot express:
 *   - the ag-grid Enterprise license key, previously
 *     `LicenseManager.setLicenseKey(environment.agGridEnterpriseKey)` at module scope -> now
 *     `AG_GRID_LICENSE_KEY`, applied once on first construction (see the token's doc for why this
 *     is not an APP_INITIALIZER: `src/bootstrap.ts` requires ag-grid-enterprise to stay in the lazy
 *     chunk).
 *   - the framework-component registry, previously the static `MiqAgCellRenderers` /
 *     `MiqAgCellEditors` maps from `ag-table-grid-options.ts` (~22 host component value-imports,
 *     the bulk of this seam's weight) -> now `AG_CELL_COMPONENTS`, merged BEFORE the
 *     `customComponents` input exactly as the two spreads were.
 *
 * Everything else resolved to a lib already: the grid option/column TYPES and the four sort
 * comparators are in @miq/shared-models, `UserInfoResolver` is in @miq/platform, and
 * `AdvancedInputFactory` came along into ../catalog.
 *
 * KNOWN PRE-EXISTING BUG carried over unchanged (reported, not fixed in this batch):
 * `autoSizeAll()` subscribes to `updateScrollbar$` on EVERY `onGridReady`, so a grid that becomes
 * ready more than once accumulates duplicate debounced subscriptions.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-ag-table',
  templateUrl: './ag-table.component.html',
  styleUrl: './ag-table.component.scss',
  exportAs: 'miqAgTable',
  imports: [
    AgScrollbarDirective,
    NgClass,
    AgGridAngular,
    StopKeydownDirective,
    MatFormField,
    MatSelect,
    FormsModule,
    MatOption,
    TitleCasePipe,
    L10nTranslatePipe
],
})
export class AgTableComponent implements OnInit {
  dataSource = input([]);
  readonly columnsInput = input<any[]>([], { alias: 'columns' });
  columns: any[] = [];
  readonly optionsInput = input<any>(undefined, { alias: 'options' });
  options: any;
  readonly gridOptionsInput = input<any>(undefined, { alias: 'gridOptions' });
  gridOptions: any;
  customComponents = input({});
  commandKey = input('ctrl');
  showAgGridTable = input<boolean>();
  gridReady = output<any>()
  isRecordListV2Enabled = input<boolean>();
  subTabType = input('');
  // viewChild to access the scrollbar directive
  scrollbarDirective = viewChild(AgScrollbarDirective);
  sortChanged = output<any>()
  columnMoved = output<any>()
  columnPinned = output<any>()
  cellValueChanged = output<any>()
  cellEditingStopped = output<any>()
  rowValueChanged = output<any>()
  rowEditingStarted = output<any>()
  rowEditingStopped = output<any>()
  gridColumnsChanged = output<any>()
  filterChanged = output<any>()
  rowSelected = output<any>()
  cellEditingStarted = output<any>()
  rowGroupOpened = output<any>()
  rowDragMove = output<any>()
  rowDragEnd = output<any>()
  rowClicked = output<any>()
  callBacks = input({});
  entity = input<string>();
  public isAutoHeight = false;
  private readonly columnGridApi: any;
  private gridApi: any;
  columnApi: any;
  frameworkComponents: any;
  tableHeight = 668;
  showAgGridNotHkGOffice: boolean = true;
  agGrid2_0Country: boolean = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly updateScrollbar$ = new Subject<void>();
  public CountryId = CountryId;
  readonly spaceEntity = 'space';
  readonly propertyEntity = 'property';
  /**
   * The shell's ag-grid framework-component registry (renderers + editors). Optional so a
   * standalone remote serve still boots — see {@link AG_CELL_COMPONENTS}.
   */
  private readonly shellCellComponents = inject(AG_CELL_COMPONENTS, { optional: true });
  private readonly agGridLicenseKey = inject(AG_GRID_LICENSE_KEY, { optional: true });
  /** Gates <ag-grid-angular> in the template until ag-grid-enterprise (modules + license) is loaded. */
  protected readonly agGridEnterpriseReady = signal(false);
  constructor(
    public translationService: L10nTranslationService,
    public userInfoResolver: UserInfoResolver
  ) {
    void ensureAgGridEnterprise(this.agGridLicenseKey).then(() => this.agGridEnterpriseReady.set(true));
    effect(() => {
      const v = this.columnsInput();
      if (v !== undefined) this.columns = v;
    });
    effect(() => {
      const v = this.optionsInput();
      if (v !== undefined) this.options = v;
    });
    effect(() => {
      const v = this.gridOptionsInput();
      if (v !== undefined) this.gridOptions = v;
    });
    effect(() => {
      const cols = this.columnsInput();
      if (!this.columnsEffectInitialized) {
        this.columnsEffectInitialized = true;
        return;
      }
      if (cols) {
        this.processColumns();
        this.updateScrollbar$.next();
      }
    });
    effect(() => {
      const key = this.commandKey();
      if (!this.commandKeyInitialized) {
        this.commandKeyInitialized = true;
        return;
      }
      if (this.gridOptions) {
        this.gridOptions.multiSortKey = this.normalizeCommandKey(key);
      }
    });
    // Setup debounced scrollbar updates
    this.updateScrollbar$.pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.showAgGridTable() && this.scrollbarDirective()) {
        this.scrollbarDirective().updateScrollbar();
      }
    });
  }
  private columnsEffectInitialized = false;
  private commandKeyInitialized = false;
  ngOnInit() {
    const gridOptionsV = this.gridOptionsInput();
    if (gridOptionsV !== undefined) this.gridOptions = gridOptionsV;
    const userCountrySkey = this.userInfoResolver.resolve()?.userCountrySkey;
    this.agGrid2_0Country = userCountrySkey != null && HK_CHINA_COUNTRY_IDS.includes(userCountrySkey);
    this.initFramework();
    this.processOptions();
    this.processGridOptions();
    this.processColumns();
    // Ensure gridOptions.multiSortKey is set from input or fallback to commandKey
    if (!this.gridOptions) this.gridOptions = {};
    if (!this.gridOptions.multiSortKey) {
      // Normalize commandKey for cross-platform compatibility
      const normalizedCommandKey = this.normalizeCommandKey(this.commandKey());
      this.gridOptions.multiSortKey = normalizedCommandKey;
    }
  }
  public pageSizeChanged($event) {
    if (this.chkObj($event)) {
      this.gridApi.setGridOption('paginationPageSize', Number($event.value));
      return;
    }
    this.gridApi.setGridOption('paginationPageSize', Number($event));
  }
  chkObj(val): boolean {
    return val instanceof Object;
  }
  private checkRowModel() {
    // If the row model type is set to server, checks if dataStream is set, otherwise changes the row type to client
    if (this.gridOptions.rowModelType === AgRowModelType.ServerSide && this.gridOptions.dataStream == null) {
      this.gridOptions.rowModelType = AgRowModelType.ClientSide;
    }
  }
  private initFramework() {
    this.frameworkComponents = {
      // renderers + editors, supplied by the shell (was the static MiqAgCellRenderers /
      // MiqAgCellEditors spread out of @shared/components/table/ag-table-grid-options)
      ...(this.shellCellComponents || {}),
      // custom to enable plugins of external OR specialized components
      ...this.customComponents(),
    };
  }
  private processColumns() {
    if (this.options && this.gridOptions && this.columns) {
      this.columns = this.configureColumns(this.columns);
    }
  }
  private processOptions() {
    this.options = { ...defaultOptions, ...this.options };
  }
  private processGridOptions() {
    this.gridOptions = { ...defaultGridOptions, ...this.gridOptions };
    this.gridOptions.noRowsToShow = this.translationService.translate('shared.noRowsToShow');
    // This value is used to identify that the table is in auto height and remove the style.height param in html
    this.isAutoHeight =
      !!this.gridOptions.domLayout && this.gridOptions.domLayout === AgGridOptionsDomLayout.AutoHeight;
    this.checkRowModel();
  }
  private configureColumns(columns: MiqAgColumn[]) {
    return columns.map(({ tooltip, children, ...column }) => {
      return children
        ? { ...column, children: this.configureColumns(children) }
        : Object.assign(
            column,
            this.mapRenderers(column),
            this.mapTooltip(tooltip),
            this.mapEditors(column),
            this.processMenuTab(column),
            this.mapSortingMethod(column)
          );
    });
  }
  private mapRenderers(column: MiqAgColumn) {
    const userRenderer = column.cellRenderer;
    return userRenderer ? { cellRenderer: userRenderer } : {};
  }
  private processMenuTab(column: MiqAgColumn) {
    return {
      menuTabs: column.menuTabs || [],
    };
  }
  private mapTooltip(tooltip: string | Function) {
    let result = {};
    // ag-grid accepts tooltip as fn, so we need to ensure it is a fn
    if (tooltip) {
      const tooltipFn = typeof tooltip !== 'function' ? () => tooltip : tooltip;
      result = {
        tooltipValueGetter: tooltipFn,
      };
    }
    return result;
  }
  private mapEditors(column: MiqAgColumn): DataObject {
    const inputType = this._getInputType(column);
    let cellEditor: string;
    if (column.hasUomField && inputType === InputType.Number) {
      cellEditor = 'agUomComponent';
    } else {
      cellEditor = InputTypeToCellEditorMap.get(inputType);
    }
    return cellEditor == null ? {} : this._makeCellEditorConfig(cellEditor, column);
  }
  /**
   * Derives what input type to use
   * @param column
   * @returns
   */
  private _getInputType(column: MiqAgColumn): InputType {
    const { dataType, inputType, lookupCategory } = column;
    // derive based on input & data type as controls module does
    const params = { dataType, inputType: inputType as InputType, lookupCategory };
    // based on combination of input and data type
    return AdvancedInputFactory.getInputType(params);
  }
  /**
   * Builds and returns an object with cellEditor name and params
   * @param cellEditor
   * @param column
   * @returns
   */
  private _makeCellEditorConfig(cellEditor: string, column: MiqAgColumn) {
    return {
      cellEditor,
      cellEditorParams: { ...column, ...column.cellEditorParams, gridOptions: this.gridOptions },
    };
  }
  /**
   * Provides comparator config to sort the column by
   * @returns Object with comparator attribute
   */
  private mapSortingMethod(column: MiqAgColumn) {
    if (typeof column?.comparator == 'function') {
      return { comparator: column?.comparator };
    }
    return {
      comparator: (leftValue, rightValue) => {
        const leftSingleValue = Array.isArray(leftValue) ? leftValue[0] : leftValue;
        const rightSingleValue = Array.isArray(rightValue) ? rightValue[0] : rightValue;
        if (typeof leftSingleValue === 'string' || typeof rightSingleValue === 'string') {
          return this._assignComparatorByValueType(leftSingleValue, rightSingleValue);
        }
        return standardComparator(leftSingleValue || '', rightSingleValue || '');
      },
    };
  }
  private _assignComparatorByValueType(leftSingleValue, rightSingleValue) {
    if (
      !isNaN(Number((leftSingleValue || '').replace(/,/g, ''))) &&
      !isNaN(Number((rightSingleValue || '').replace(/,/g, '')))
    ) {
      // Handling numbers with comma separator
      return numberComparator(
        (leftSingleValue || '-1').replace(/,/g, ''),
        (rightSingleValue || '-1').replace(/,/g, '')
      );
    } else if (!isNaN(Date.parse(leftSingleValue))) {
      return dateComparator(leftSingleValue, rightSingleValue);
    } else {
      return stringComparator(leftSingleValue || '', rightSingleValue || '');
    }
  }
  private createDataSource(functionStream: (tableParams: AgStreamParams) => Observable<AgStreamResult>) {
    return {
      getRows: function getTableRows(params: IServerSideGetRowsParams) {
        const {
          request: { startRow, endRow, sortModel, filterModel },
        } = params;
        const request: AgStreamParams = {
          startRow,
          endRow,
          sort: sortModel,
          filters: filterModel as { [key: string]: AgFilterParams },
        };
        functionStream(request)
          .pipe(observeOn(asyncScheduler, 0))
          .subscribe(
            (result: AgStreamResult) => {
              params.success({
                rowData: result.data,
                rowCount: result.totalRows,
              });
            },
            error => {
              params.fail();
            }
          );
      },
    };
  }
  private bindDataStream({ api }) {
    const isServerSide =
      this.gridOptions.rowModelType === AgRowModelType.ServerSide && this.gridOptions.dataStream != null;
    if (isServerSide) {
      const dataSource = this.createDataSource(this.gridOptions.dataStream);
      api.setServerSideDatasource(dataSource);
    }
  }
  /**
   * AG Grid ready event handler.
   */
  onGridReady(params: any) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    if (this.gridOptions.autoSizeAll) {
      this.autoSizeAll();
    }
    this.bindDataStream(params);
    // Refresh the top scrollbar after grid is ready
    this.updateScrollbar$.next();
    this.gridReady.emit(params);
  }
  /**
   * Auto-sizes all columns if columnApi is available.
   */
  private autoSizeAll(): void {
    if (!this.columnApi?.getAllColumns) return;
    const allColumnIds = this.columnApi.getAllColumns().map((col: any) => col.getColId());
    this.columnApi.autoSizeColumns(allColumnIds, false);
    // Refresh scrollbar after auto-sizing with longer debounce
    this.updateScrollbar$.pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.showAgGridTable() && this.scrollbarDirective()) {
        this.scrollbarDirective().updateScrollbar();
      }
    });
  }
  /**
   * Get the AG Grid API instance
   */
  public getGridApi() {
    return this.gridApi;
  }
  /**
   * Get the column API instance
   */
  public getColumnApi() {
    return this.columnApi;
  }
  /**
   * Normalize command key for cross-platform compatibility
   */
  private normalizeCommandKey(commandKey: string): string {
    if (!commandKey) return 'ctrl';
    const normalized = commandKey.toLowerCase().trim();
    // Map common variations to standard AG Grid values
    const keyMap: { [key: string]: string } = {
      ctrl: 'ctrl',
      control: 'ctrl',
      cmd: 'meta',
      command: 'meta',
      meta: 'meta',
      alt: 'alt',
      shift: 'shift',
    };
    return keyMap[normalized] || 'ctrl';
  }
}
